import type { Route } from "../../../utils/models/Route";

/**
 * Stage connectors — pure detection logic.
 *
 * When two consecutive routes (stages) in a plan have the previous route's
 * END marker and the next route's START marker within CONNECTOR_THRESHOLD_M,
 * the planner shows a single draggable "connector" handle between them.
 * Dragging it moves BOTH endpoints and re-routes the previous route's last
 * segment and the next route's first segment together.
 *
 * This module is deliberately side-effect free (no map, no store) so it can
 * be unit tested — rendering and drag behaviour live in
 * store/modules/actions/stage-connectors.ts.
 */

// Endpoints further apart than this are NOT connected (hard cutoff).
export const CONNECTOR_THRESHOLD_M = 150;

// Endpoints closer than this are treated as a single shared point.
export const CONNECTOR_COINCIDENT_M = 1;

export interface StageConnector {
  /** Stable id: `${prevRouteKey}::${nextRouteKey}` */
  id: string;
  /** Key of the earlier route in state.routes */
  prevRouteKey: string;
  /** Key of the later route in state.routes */
  nextRouteKey: string;
  /** Index of the previous route's end marker (markers.length - 1) */
  prevMarkerIndex: number;
  /** Index of the next route's start marker (always 0) */
  nextMarkerIndex: number;
  /** [lon, lat] of the previous route's end marker */
  prevEnd: [number, number];
  /** [lon, lat] of the next route's start marker */
  nextStart: [number, number];
  /** [lon, lat] midpoint between the two endpoints — where the handle renders */
  position: [number, number];
  /** Straight-line distance between the two endpoints in meters */
  gapMeters: number;
  /** True when the endpoints are effectively the same point */
  coincident: boolean;
  /**
   * The day number of the PREVIOUS stage — the connector marks the end of
   * that day. This is the SAME numbering system used by the day list's badge
   * and the elevation profile's day markers (not a positional count of
   * connectors, which would drift from the real day number once days are
   * added/removed out of order or a day is deleted — see the "gap" test).
   */
  stageNumber: number;
  /**
   * The day number of the NEXT stage — the day that STARTS at this
   * connector. This is the number the map's marker shows: a badge reading
   * "2" marks where day 2 begins. For a join between two routes of the same
   * day it equals stageNumber.
   */
  nextDayNumber: number;
}

/** Straight-line distance in meters between two [lon, lat] coordinates. */
export function haversineMeters(a: number[], b: number[]): number {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * toRad;
  const dLon = (b[0] - a[0]) * toRad;
  const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(a[1] * toRad) * Math.cos(b[1] * toRad) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * A route can participate in a connector when it is part of the MAIN route
 * line. Only 'route'-style routes qualify — alts, spurs and transport legs
 * are offshoots of the main line and never connect (nor block the chain).
 * Referenced hikes (e.g. an imported multi-day hike) ARE part of the main
 * line — dragging their connector is gated by the reference-edit warning in
 * the interaction layer, not excluded here.
 */
export function isConnectableRoute(route: Route | undefined | null, minMarkers: number = 2): boolean {
  if (!route) return false;
  if (!route.markers || route.markers.length < minMarkers) return false;
  if ((route.route_style ?? 'route') !== 'route') return false;
  return true;
}

/** Main-line routes (see isConnectableRoute) in position order. */
function orderedMainLineRoutes(routes: { [routeKey: string]: Route }, minMarkers: number = 2) {
  return Object.keys(routes)
    .map(key => ({ key, route: routes[key] }))
    .filter(entry => isConnectableRoute(entry.route, minMarkers))
    .sort((a, b) => a.route.position - b.route.position);
}

export interface DayStage {
  dayNumber: number;
  /** First main-line route of the day — carries the day's START */
  firstKey: string;
  firstRoute: Route;
  /** Last main-line route of the day — carries the day's END (the stage marker) */
  lastKey: string;
  lastRoute: Route;
}

/**
 * A STAGE is a day's worth of main-line routes. When a day contains several
 * 'route'-style routes, its stage end is the LAST listed one and its stage
 * start is the FIRST — intra-day mains never connect to each other.
 * Routes without a day default to day 1.
 *
 * `minMarkers` defaults to 2 (a real, routable pair of endpoints) — what
 * stage CONNECTORS need, since a connector measures the gap between two
 * routes' endpoints. Callers that only care about a day's current end/start
 * point in isolation (attaching it to a POI/waypoint/highlight) pass 1, so a
 * route that's still just its first-placed marker — no line yet — still
 * counts as its day's stage instead of being invisible until a second
 * marker exists.
 */
export function orderedDayStages(routes: { [routeKey: string]: Route }, minMarkers: number = 2): DayStage[] {
  const byDay = new Map<number, Array<{ key: string; route: Route }>>();
  for (const entry of orderedMainLineRoutes(routes, minMarkers)) {
    const dayNumber = entry.route.day ?? 1;
    if (!byDay.has(dayNumber)) byDay.set(dayNumber, []);
    byDay.get(dayNumber)!.push(entry);
  }

  return Array.from(byDay.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([dayNumber, entries]) => ({
      dayNumber,
      firstKey: entries[0].key,
      firstRoute: entries[0].route,
      lastKey: entries[entries.length - 1].key,
      lastRoute: entries[entries.length - 1].route,
    }));
}

/**
 * Detect connectors between consecutive DAY STAGES: the previous day's last
 * main route END joins the next day's first main route START. Alts, spurs
 * and transport legs are offshoots and never participate; extra mains within
 * a day are part of that day's stage, not stages of their own. Whether two
 * stages connect is decided purely by the distance between their endpoints —
 * except pairs the user explicitly disconnected (`excludedIds`, matched
 * against the connector id `${prevKey}::${nextKey}`), which never connect
 * regardless of distance.
 */
export function detectStageConnectors(
  routes: { [routeKey: string]: Route },
  thresholdM: number = CONNECTOR_THRESHOLD_M,
  excludedIds: ReadonlyArray<string> = []
): StageConnector[] {
  const stages = orderedDayStages(routes);

  const connectors: StageConnector[] = [];

  for (let i = 0; i < stages.length - 1; i++) {
    const prev = { key: stages[i].lastKey, route: stages[i].lastRoute };
    const next = { key: stages[i + 1].firstKey, route: stages[i + 1].firstRoute };

    if (excludedIds.includes(`${prev.key}::${next.key}`)) continue;

    const prevMarkerIndex = prev.route.markers.length - 1;
    const prevEndCoords = prev.route.markers[prevMarkerIndex]?.markerGeometry?.coordinates;
    const nextStartCoords = next.route.markers[0]?.markerGeometry?.coordinates;
    if (!prevEndCoords || !nextStartCoords) continue;

    const gapMeters = haversineMeters(prevEndCoords, nextStartCoords);
    if (gapMeters > thresholdM) continue;

    const prevEnd: [number, number] = [prevEndCoords[0], prevEndCoords[1]];
    const nextStart: [number, number] = [nextStartCoords[0], nextStartCoords[1]];

    connectors.push({
      id: `${prev.key}::${next.key}`,
      prevRouteKey: prev.key,
      nextRouteKey: next.key,
      prevMarkerIndex,
      nextMarkerIndex: 0,
      prevEnd,
      nextStart,
      position: [(prevEnd[0] + nextStart[0]) / 2, (prevEnd[1] + nextStart[1]) / 2],
      gapMeters,
      coincident: gapMeters < CONNECTOR_COINCIDENT_M,
      stageNumber: stages[i].dayNumber,
      nextDayNumber: stages[i + 1].dayNumber,
    });
  }

  return connectors;
}

/**
 * Joins WITHIN a day: consecutive main-line routes of the same day whose end
 * and start are within `thresholdM`. A day of several routes (Day 1: Route 1,
 * Route 2) reads as one continuous stage, so Route 1's end joins Route 2's
 * start the same way the day's last route joins the next day's first — drawn
 * with a smaller handle than a day connector, and with no stage number.
 * Same shape as a StageConnector so the connector drag and preview apply.
 */
export function detectRouteJoins(
  routes: { [routeKey: string]: Route },
  thresholdM: number = CONNECTOR_THRESHOLD_M,
  excludedIds: ReadonlyArray<string> = []
): StageConnector[] {
  const byDay = new Map<number, Array<{ key: string; route: Route }>>();
  for (const entry of orderedMainLineRoutes(routes)) {
    const day = entry.route.day ?? 1;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(entry);
  }

  const joins: StageConnector[] = [];
  for (const [dayNumber, entries] of byDay) {
    for (let i = 0; i < entries.length - 1; i++) {
      const prev = entries[i];
      const next = entries[i + 1];
      const id = `${prev.key}::${next.key}`;
      if (excludedIds.includes(id)) continue;

      const prevMarkerIndex = prev.route.markers.length - 1;
      const prevEndCoords = prev.route.markers[prevMarkerIndex]?.markerGeometry?.coordinates;
      const nextStartCoords = next.route.markers[0]?.markerGeometry?.coordinates;
      if (!prevEndCoords || !nextStartCoords) continue;

      const gapMeters = haversineMeters(prevEndCoords, nextStartCoords);
      if (gapMeters > thresholdM) continue;

      const prevEnd: [number, number] = [prevEndCoords[0], prevEndCoords[1]];
      const nextStart: [number, number] = [nextStartCoords[0], nextStartCoords[1]];
      joins.push({
        id,
        prevRouteKey: prev.key,
        nextRouteKey: next.key,
        prevMarkerIndex,
        nextMarkerIndex: 0,
        prevEnd,
        nextStart,
        position: [(prevEnd[0] + nextStart[0]) / 2, (prevEnd[1] + nextStart[1]) / 2],
        gapMeters,
        coincident: gapMeters < CONNECTOR_COINCIDENT_M,
        stageNumber: dayNumber,
        nextDayNumber: dayNumber,
      });
    }
  }
  return joins;
}

/**
 * Screen-space radius, in CSS pixels, within which a dragged stage endpoint
 * magnetically snaps onto the endpoint it can join. Pixels rather than metres
 * so the gesture feels the same at every zoom level.
 */
export const SNAP_CONNECT_PX = 24;

/** Is the pointer close enough to the snap target to engage? */
export function isWithinSnapRadius(
  targetPx: { x: number; y: number },
  pointerPx: { x: number; y: number },
  radiusPx: number = SNAP_CONNECT_PX
): boolean {
  const dx = targetPx.x - pointerPx.x;
  const dy = targetPx.y - pointerPx.y;
  return Math.sqrt(dx * dx + dy * dy) <= radiusPx;
}

export interface StageEndMarker {
  /** Key of the main-line route whose END this marker sits on */
  routeKey: string;
  /** The day number this stage end belongs to (see StageConnector.stageNumber) */
  stageNumber: number;
  /**
   * The day that STARTS here — the first started day after stageNumber. The
   * marker's badge shows this number (see StageConnector.nextDayNumber).
   */
  nextDayNumber: number;
  /** [lon, lat] where the marker renders (connector midpoint when connected) */
  position: [number, number];
  /** The connector when this stage joins the next one; null when floating */
  connector: StageConnector | null;
}

/**
 * The first started day after `dayNumber` — the day that begins where
 * `dayNumber` ends. Real day numbers, so a gap (day 1 → day 3) reads 3.
 */
export function nextStartedDay(startedDays: number[], dayNumber: number): number {
  return Math.min(...startedDays.filter(candidate => candidate > dayNumber));
}

/**
 * Every day stage that has a FOLLOWING stage gets an end-of-stage marker on
 * its LAST main route: a draggable connector handle when the next stage
 * starts nearby, or a static ("floating") numbered marker on the route's own
 * end point when it doesn't. The marker is numbered with the day that STARTS
 * there (nextDayNumber) — "2" marks where day 2 begins. A following stage
 * counts as soon as a later day has a main route with its first marker
 * placed — adding a new day's start point immediately turns the previous
 * stage's end into a numbered stage marker. The final stage's end is the
 * trail finish (separate pin, no number).
 */
export function detectStageEndMarkers(
  routes: { [routeKey: string]: Route },
  thresholdM: number = CONNECTOR_THRESHOLD_M,
  excludedIds: ReadonlyArray<string> = []
): StageEndMarker[] {
  const stages = orderedDayStages(routes);
  const connectorsByPrevKey = new Map(
    detectStageConnectors(routes, thresholdM, excludedIds).map(connector => [connector.prevRouteKey, connector])
  );

  // Days that have at least a STARTED (≥1 marker) main route — a started
  // later day makes the stage before it a boundary, not the trail finish.
  const startedMainDays = Object.keys(routes)
    .map(key => routes[key])
    .filter(route => route &&
      (route.route_style ?? 'route') === 'route' &&
      route.markers && route.markers.length >= 1)
    .map(route => route.day ?? 1);

  return stages
    .filter(stage => startedMainDays.some(dayNumber => dayNumber > stage.dayNumber))
    .map((stage) => {
      const connector = connectorsByPrevKey.get(stage.lastKey) ?? null;
      const endCoords = stage.lastRoute.markers[stage.lastRoute.markers.length - 1].markerGeometry.coordinates;
      return {
        routeKey: stage.lastKey,
        stageNumber: stage.dayNumber,
        nextDayNumber: nextStartedDay(startedMainDays, stage.dayNumber),
        position: connector
          ? connector.position
          : [endCoords[0], endCoords[1]] as [number, number],
        connector,
      };
    });
}

export interface ReconnectableStageLink {
  /** Day number of the pair's LATER stage — the day being reconnected */
  dayNumber: number;
  /** Day number of the pair's EARLIER stage */
  prevDayNumber: number;
  prevRouteKey: string;
  nextRouteKey: string;
}

/**
 * Day-stage boundaries that are NOT currently connected — either their
 * endpoints sit further apart than the threshold, or the pair was explicitly
 * disconnected (excludedIds). This is the mirror image of
 * detectStageConnectors: it drives a "Reconnect to Day N" affordance the way
 * detectStageConnectors' output drives "Disconnect".
 */
export function findReconnectableStageLinks(
  routes: { [routeKey: string]: Route },
  thresholdM: number = CONNECTOR_THRESHOLD_M,
  excludedIds: ReadonlyArray<string> = []
): ReconnectableStageLink[] {
  const stages = orderedDayStages(routes);
  const connectedPairIds = new Set(
    detectStageConnectors(routes, thresholdM, excludedIds).map(c => `${c.prevRouteKey}::${c.nextRouteKey}`)
  );

  const links: ReconnectableStageLink[] = [];
  for (let i = 0; i < stages.length - 1; i++) {
    const prev = stages[i];
    const next = stages[i + 1];
    if (connectedPairIds.has(`${prev.lastKey}::${next.firstKey}`)) continue;

    links.push({
      dayNumber: next.dayNumber,
      prevDayNumber: prev.dayNumber,
      prevRouteKey: prev.lastKey,
      nextRouteKey: next.firstKey,
    });
  }
  return links;
}

export interface SnapConnectTarget {
  /**
   * Key of the OTHER route that owns the snap target point — the next
   * stage's first route when snapping forward, the previous stage's last
   * route when snapping backward.
   */
  routeKey: string;
  /** [lon, lat] of the target endpoint */
  position: [number, number];
  /** Connector id (`prevKey::nextKey`) the snap would create */
  connectorId: string;
}

/**
 * When dragging a marker, can it snap-connect to an adjacent stage? Works in
 * BOTH directions:
 *  - FORWARD: the END marker of a day-stage's LAST main route snaps onto the
 *    FIRST main route of the NEXT day-stage.
 *  - BACKWARD: the START marker of a day-stage's FIRST main route snaps onto
 *    the LAST main route of the PREVIOUS day-stage.
 * Mains only, never alts/spurs/transport. Distance is NOT checked here; the
 * interaction layer decides the snap radius in screen pixels while dragging.
 */
export function findSnapConnectTarget(
  routes: { [routeKey: string]: Route },
  routeKey: string,
  markerIndex: number
): SnapConnectTarget | null {
  const route = routes[routeKey];
  if (!isConnectableRoute(route)) return null;

  const stages = orderedDayStages(routes);

  if (markerIndex === route.markers.length - 1) {
    const stageIndex = stages.findIndex(stage => stage.lastKey === routeKey);
    if (stageIndex < 0 || stageIndex >= stages.length - 1) return null;

    const nextStage = stages[stageIndex + 1];
    const startCoords = nextStage.firstRoute.markers[0]?.markerGeometry?.coordinates;
    if (!startCoords) return null;

    return {
      routeKey: nextStage.firstKey,
      position: [startCoords[0], startCoords[1]],
      connectorId: `${routeKey}::${nextStage.firstKey}`,
    };
  }

  if (markerIndex === 0) {
    const stageIndex = stages.findIndex(stage => stage.firstKey === routeKey);
    if (stageIndex <= 0) return null;

    const prevStage = stages[stageIndex - 1];
    const prevMarkerIndex = prevStage.lastRoute.markers.length - 1;
    const endCoords = prevStage.lastRoute.markers[prevMarkerIndex]?.markerGeometry?.coordinates;
    if (!endCoords) return null;

    return {
      routeKey: prevStage.lastKey,
      position: [endCoords[0], endCoords[1]],
      connectorId: `${prevStage.lastKey}::${routeKey}`,
    };
  }

  return null;
}
